import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { compose } from 'recompose';
import { Tooltip } from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import { UserContext } from '@/components/App';

import ShipperTeammateProfile from './ShipperTeammateProfile';
import CarrierTeammateProfile from './CarrierTeammateProfile';
import Team from './Team';
import Stores from '../ShipperAccount/Stores';
import Billing from '../ShipperAccount/Billing';
import Password from '../Password';
import Warehouse from '../CarrierAccount/Warehouse';
import Teammates from '../Teammates';
import AccountCardSetup from '../ShipperAccount/AccountCardSetup';
import * as ROLES from '../../../constants/roles';
import { AGGREGATE_RATING_BY_DRIVER } from '@/graphql/queries/ratings';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useClientUser } from '@/hooks';
import ProfileImageUpload from '../ProfileImageUpload';
import { TRUCKS_TAB, useTrucksTab } from '../hooks/useTrucksTab';

import api from '@/utilities/api';
import LoadPreferences from '../CarrierAccount/LoadPreferences';
import ShippingPartners from '../ShippingPartners';
import { LIST_TARIFFS_BY_CLIENT_ID, UPSERT_DRIVER_TARIFFS, UPSERT_PRICING_OVERRIDES } from '../Tariffs/graphql';
import * as Sentry from '@sentry/react';
import CustomTags from '../CustomTags';
import ServiceLevels from '../ServiceLevels';
import TariffTable from '../TariffTable';
import CustomDocuments from '../CustomDocuments';
import ArchiveDocuments from '../ArchiveDocuments';
import SKUsAndServices from '../SKUsAndServices';
import SLARequirements from '../SLARequirements';
import MilestoneThresholds from '../MilestoneThresholds';
import EmailSubscriptions from '../EmailSubscriptions';
import Surveys from '../Surveys';
import PartnerCarriers from '../ShipperAccount/PartnerCarriers';
import { TabCard } from '../blocks';
import { css } from '@emotion/react';

function TeammateAccountPage(props) {
    const { user, client, sidebarCollapsed } = useContext(UserContext);
    const { accountType, businessName, client_id, email, isImposter, teammateRoles, writableTariffClientIds } =
        useClientUser();

    const onSelectTab = useTrucksTab();
    const isShipperAdminTeammate = () => teammateRoles?.admin && accountType === 'shipper';
    const isShipperViewerTeammate = () => teammateRoles?.view && accountType === 'shipper';
    const isAdminTeammate = () => !!teammateRoles?.admin;

    const isCarrierAdminTeammate = () => teammateRoles?.admin && accountType === 'carrier';

    const [fetchOverallRating, { data: ratings }] = useLazyQuery(AGGREGATE_RATING_BY_DRIVER);

    const [fetchTariffs, { data }] = useLazyQuery(LIST_TARIFFS_BY_CLIENT_ID, {
        variables: {
            client_ids: writableTariffClientIds,
        },
    });

    const [upsertDriverTariff] = useMutation(UPSERT_DRIVER_TARIFFS, {
        update: (cache, { data: { updated } }) => {
            cache.updateQuery(
                {
                    query: LIST_TARIFFS_BY_CLIENT_ID,
                    variables: {
                        client_ids: [
                            ...writableTariffClientIds,
                        ],
                    },
                },
                (data) => {
                    let { driver_tariffs, ...rest } = data;
                    let clone = [...driver_tariffs];
                    const update = updated.returning[0];

                    const idx = clone.findIndex((m) => m.mapping_id === update.mapping_id);
                    if (idx >= 0) {
                        clone[idx] = update;
                    } else {
                        clone = [...clone, update];
                    }

                    return {
                        ...rest,
                        driver_tariffs: clone,
                    };
                }
            );
        },
    });

    const [upsertPo] = useMutation(UPSERT_PRICING_OVERRIDES, {
        update: (cache, { data: { updated } }) => {
            cache.updateQuery(
                {
                    query: LIST_TARIFFS_BY_CLIENT_ID,
                    variables: {
                        client_ids: [
                            ...writableTariffClientIds,
                        ],
                    },
                },
                (data) => {
                    let { overrides, ...rest } = data;
                    let clone = [...overrides];
                    const [update] = updated;

                    const idx = clone.findIndex((po) => po.pricing_override_id === update.pricing_override_id);
                    if (idx >= 0) {
                        clone[idx] = update;
                    } else {
                        clone = [...clone, update];
                    }

                    return {
                        ...rest,
                        overrides: clone,
                    };
                }
            );
        },
        onError: (e) => {
            Sentry.captureException(e);
        },
    });

    useEffect(() => {
        if (user?.user_id) {
            fetchOverallRating({ variables: { driver_id: user.user_id } });
            fetchTariffs();
        }
    }, [user]);

    const [tariffs, overrides, driverMappings] = useMemo(() => {
        if (data) {
            const { tariffs, overrides, driver_tariffs } = data;
            return [tariffs, overrides, driver_tariffs];
        }

        return [[], [], []];
    }, [data]);

    const rating = useMemo(() => {
        if (ratings?.results?.aggregate?.avg?.rating) {
            return ratings?.results?.aggregate?.avg?.rating;
        }

        return 0;
    }, [ratings]);

    return (
        <div className={`${sidebarCollapsed && 'collapse-margin'} sidenav-margin-responsive`}>
            <div className="d-flex justify-content-between my-3">
                <h2 className="  accountwidth" style={{ fontWeight: '700' }}>
                    My Account
                </h2>
                {teammateRoles?.driver && rating >= 3 && (
                    <div className="align-self-center d-flex">
                        <h5 className="text-nowrap">My Rating: </h5>
                        <Tooltip
                            title={rating.toFixed(1) + ' / 5'}
                            arrow
                            PopperProps={{ style: { marginTop: '-10px' } }}
                        >
                            <div>
                                <Rating readOnly value={rating} precision={0.1} />
                            </div>
                        </Tooltip>
                    </div>
                )}
            </div>
            <div className="align-self-center d-flex">Team: {businessName}</div>
            <Tabs defaultActiveKey="Profile" onSelect={onSelectTab}>
                <Tab tabClassName="tabLink" eventKey="Profile" title="Profile">
                    {accountType === 'shipper' && <ShipperTeammateProfile user={user} isImposter={isImposter} />}

                    {isShipperAdminTeammate() && <ProfileImageUpload client={client} />}

                    {accountType === 'carrier' && <CarrierTeammateProfile user={user} isImposter={isImposter} />}
                </Tab>

                {(isShipperAdminTeammate() || isShipperViewerTeammate()) && (
                    <Tab tabClassName="tabLink" eventKey="Stores" title="Stores">
                        <Stores
                            viewOnly={isShipperViewerTeammate()}
                            currentClientRef={client_id}
                            currentClient={client}
                        />
                    </Tab>
                )}
                {isCarrierAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Warehouse" title="Warehouse Details">
                        <Stores viewOnly={false} currentClientRef={client_id} currentClient={client} />
                    </Tab>
                )}
                {isAdminTeammate() && <Tab tabClassName="tabLink" eventKey={TRUCKS_TAB} title="Trucks" />}

                {isShipperAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="SKUs" title="SKU's / Services">
                        <SKUsAndServices clientRef={client_id} />
                    </Tab>
                )}

                {isCarrierAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Load Preferences" title="Load Preferences">
                        <LoadPreferences currentCarrier={client} />
                    </Tab>
                )}

                {isCarrierAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Shippers" title="Shippers">
                        <ShippingPartners
                            client={client}
                            tariffs={tariffs}
                            overrides={overrides}
                            callbacks={{ upsertPo }}
                        />
                    </Tab>
                )}

                {(isShipperAdminTeammate() || isShipperViewerTeammate()) && (
                    <Tab tabClassName="tabLink" eventKey="Billing" title="Billing">
                        <Billing currentShipper={client} />
                    </Tab>
                )}
                {isAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Teammates" title="Teammates">
                        <Teammates
                            currentUser={{
                                business: businessName,
                                email: email,
                            }}
                            teamLeaderType={accountType}
                            tariffs={tariffs}
                            driverMappings={driverMappings}
                            callbacks={{ upsertDriverTariff }}
                        />
                    </Tab>
                )}

                {isCarrierAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Tags" title="Tags">
                        <CustomTags />
                    </Tab>
                )}

                {isAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Custom Documents" title="Custom Documents">
                        <CustomDocuments />
                        <ArchiveDocuments client_id={client_id} isAdmin={false} editable={true} />
                        <TabCard
                            css={css`
                                padding: 2rem;
                            `}
                        >
                            <ArchiveDocuments client_id={client_id} isAdmin={true} editable={false} />
                        </TabCard>
                    </Tab>
                )}

                {isShipperAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="SLA Requirements" title="SLA Requirements">
                        <SLARequirements />
                    </Tab>
                )}

                {isShipperAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Milestone Thresholds" title="Milestone Thresholds">
                        <MilestoneThresholds client={client} />
                    </Tab>
                )}

                {isShipperAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Email Subscriptions" title="Email Subscriptions">
                        <EmailSubscriptions client={client} />
                    </Tab>
                )}

                {isCarrierAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Service Types" title="Service Types">
                        <ServiceLevels />
                    </Tab>
                )}

                {isAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Tariffs" title="Tariffs">
                        <TariffTable
                            client={client}
                            tariffs={tariffs}
                            overrides={overrides}
                            driverMappings={driverMappings}
                            callbacks={{ upsertPo, upsertDriverTariff }}
                        />
                    </Tab>
                )}

                {isCarrierAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Billing" title="Billing">
                        <AccountCardSetup currentClient={client} />
                    </Tab>
                )}
                {isShipperAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Surveys" title="Surveys">
                        <Surveys />
                    </Tab>
                )}

                {isShipperAdminTeammate() && (
                    <Tab tabClassName="tabLink" eventKey="Partner Carriers" title="Partner Carriers">
                        <PartnerCarriers />
                    </Tab>
                )}

                <Tab tabClassName="tabLink" eventKey="Team" title="Team">
                    <Team />
                </Tab>
                <Tab tabClassName="tabLink" eventKey="Password" title="Password">
                    <Password />
                </Tab>
            </Tabs>
        </div>
    );
}

export default TeammateAccountPage;
